/**
 * Página de Assinatura do Estabelecimento (/assinatura)
 *
 * Permite ao dono contratar um plano (assinatura recorrente no gateway da plataforma),
 * acompanhar o status, concluir um pagamento pendente e cancelar a assinatura.
 *
 * Fica fora do layout principal de propósito: continua acessível quando o
 * acesso ao painel está bloqueado por período de teste/assinatura expirado.
 *
 * @author Equipe VendeAi
 * @since 1.2.0
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  Loader2,
  LogOut,
  RefreshCw,
  ExternalLink,
  ShieldCheck,
  MessageCircle,
} from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { logoHorizontalLight, logoHorizontalDark } from '@assets/index';
import {
  BillingCycle,
  BillingSummary,
  cancelBilling,
  formatBRL,
  getApiErrorMessage,
  getBillingSummary,
  startBillingCheckout,
  syncBilling,
  TenantSubscriptionStatus,
} from '@services/platformService';
import { invalidatePlanAccess } from '@hooks/usePlanAccess';

const STATUS_INFO: Record<
  TenantSubscriptionStatus,
  { label: string; className: string; icon: typeof Clock }
> = {
  TRIAL: {
    label: 'Período de teste',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
    icon: Clock,
  },
  ACTIVE: {
    label: 'Assinatura ativa',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
    icon: CheckCircle2,
  },
  PAST_DUE: {
    label: 'Pagamento pendente',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
    icon: AlertTriangle,
  },
  CANCELLED: {
    label: 'Cancelada',
    className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    icon: XCircle,
  },
  EXPIRED: {
    label: 'Acesso expirado',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    icon: XCircle,
  },
};

/** Máscara de CPF/CNPJ */
function maskDocument(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })
    : '—';

export function Subscription() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [cycle, setCycle] = useState<BillingCycle>('MONTHLY');
  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const returnHandled = useRef(false);
  const [payerDocument, setPayerDocument] = useState('');
  const [documentError, setDocumentError] = useState<string | null>(null);

  const isOwner = user?.role === 'OWNER';

  const load = useCallback(async (sync = false) => {
    try {
      const data = sync ? await syncBilling() : await getBillingSummary();
      setSummary(data);
      // Plano/módulos podem ter mudado: atualiza o menu e os bloqueios
      invalidatePlanAccess();
      return data;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 403) setForbidden(true);
      else toast.error(getApiErrorMessage(error, 'Não foi possível carregar sua assinatura'));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Retorno do pagamento: sincroniza o status imediatamente
  useEffect(() => {
    const isReturn = searchParams.get('retorno') === '1';
    if (isReturn && !returnHandled.current) {
      returnHandled.current = true;
      setSearchParams({}, { replace: true });
      load(true).then(data => {
        if (data?.subscription?.status === 'AUTHORIZED') {
          toast.success('Assinatura confirmada! Sua loja está ativa.');
        } else if (data?.subscription?.status === 'PENDING') {
          toast('Pagamento em processamento. Assim que for aprovado, sua loja será ativada.', {
            icon: '⏳',
          });
        }
      });
      return;
    }
    if (!returnHandled.current) load(false);
  }, [load, searchParams, setSearchParams]);

  const hasYearly = useMemo(
    () => summary?.plans.some(p => p.priceYearly && p.priceYearly > 0) ?? false,
    [summary],
  );
  const tenantStatus = summary?.tenant.status;
  const panelBlocked = tenantStatus === 'EXPIRED';
  const activeSubscription =
    summary?.subscription && ['AUTHORIZED', 'PAUSED'].includes(summary.subscription.status)
      ? summary.subscription
      : null;
  const pendingSubscription =
    summary?.subscription?.status === 'PENDING' ? summary.subscription : null;

  const handleSubscribe = async (planSlug: string) => {
    if (busyPlan) return;
    const documentDigits = payerDocument.replace(/\D/g, '');
    if (summary?.documentRequired && ![11, 14].includes(documentDigits.length)) {
      setDocumentError('Informe o CPF ou CNPJ para emitir as cobranças');
      document.getElementById('payer-document')?.focus();
      return;
    }
    setBusyPlan(planSlug);
    try {
      const result = await startBillingCheckout(planSlug, cycle, documentDigits || undefined);
      const scheduled = new Date(result.firstPaymentDate).getTime() > Date.now() + 60 * 60 * 1000;
      if (scheduled) {
        // Assinou durante o teste: a 1ª cobrança só acontece no fim do teste
        toast.success(
          `Assinatura agendada! A primeira cobrança será em ${formatDate(result.firstPaymentDate)}.`,
        );
        await load(false);
        setBusyPlan(null);
        return;
      }
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      toast.success('Assinatura criada! Você receberá a cobrança por e-mail.');
      await load(false);
      setBusyPlan(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Não foi possível iniciar a assinatura'));
      setBusyPlan(null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    const data = await load(true);
    setSyncing(false);
    if (data?.subscription?.status === 'AUTHORIZED') toast.success('Assinatura ativa!');
    else if (data) toast('Status atualizado', { icon: '🔄' });
  };

  const handleCancel = async () => {
    if (
      !confirm(
        'Cancelar a assinatura? A cobrança recorrente será interrompida e sua loja continua funcionando até o fim do período já pago.',
      )
    ) {
      return;
    }
    setCancelling(true);
    try {
      setSummary(await cancelBilling());
      toast.success('Assinatura cancelada');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Não foi possível cancelar'));
    } finally {
      setCancelling(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/entrar', { replace: true });
  };

  const statusInfo = tenantStatus ? STATUS_INFO[tenantStatus] : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="border-b border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <img src={logoHorizontalLight} alt="Vende Aí" className="h-6 w-auto dark:hidden" />
            <img src={logoHorizontalDark} alt="Vende Aí" className="hidden h-6 w-auto dark:block" />
          </div>
          <div className="flex items-center gap-2">
            {!panelBlocked && !forbidden && (
              <Link to="/painel" className="btn btn-ghost text-sm">
                <ArrowLeft size={16} /> Voltar ao painel
              </Link>
            )}
            <button onClick={handleLogout} className="btn btn-ghost text-sm">
              <LogOut size={16} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 px-4 py-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Assinatura</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Mensalidade fixa, pedidos ilimitados e sem comissão. Cancele quando quiser.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2
              size={32}
              className="animate-spin text-secondary-700"
              aria-label="Carregando"
            />
          </div>
        ) : forbidden ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100">
            <p className="font-semibold">O acesso da sua loja está suspenso.</p>
            <p className="mt-1 text-sm">
              Peça ao responsável pelo estabelecimento para regularizar a assinatura.
            </p>
          </div>
        ) : summary ? (
          <>
            {/* Situação atual */}
            <section className="rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  {statusInfo && (
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${statusInfo.className}`}
                    >
                      <statusInfo.icon size={16} /> {statusInfo.label}
                    </span>
                  )}
                  <p className="mt-3 text-lg font-semibold text-gray-900 dark:text-white">
                    {summary.tenant.plan
                      ? `Plano ${summary.tenant.plan.name}`
                      : 'Nenhum plano contratado'}
                  </p>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {tenantStatus === 'TRIAL' &&
                      !pendingSubscription &&
                      `Teste grátis até ${formatDate(summary.tenant.endsAt)} (${summary.tenant.daysLeft ?? 0} dia(s) restantes).`}
                    {tenantStatus === 'TRIAL' &&
                      pendingSubscription &&
                      `Plano ${pendingSubscription.planName} assinado. A primeira cobrança de ${formatBRL(pendingSubscription.amount)} vence em ${formatDate(pendingSubscription.nextPaymentDate)}; até lá você continua no período de teste.`}
                    {tenantStatus === 'ACTIVE' &&
                      activeSubscription?.nextPaymentDate &&
                      `Próxima cobrança em ${formatDate(activeSubscription.nextPaymentDate)}: ${formatBRL(activeSubscription.amount)}.`}
                    {tenantStatus === 'ACTIVE' &&
                      !activeSubscription &&
                      summary.tenant.endsAt &&
                      `Válida até ${formatDate(summary.tenant.endsAt)}.`}
                    {tenantStatus === 'PAST_DUE' &&
                      'Há uma mensalidade em aberto. Pague a fatura para evitar o bloqueio da loja (tolerância de 5 dias após o vencimento).'}
                    {tenantStatus === 'CANCELLED' &&
                      `Sua loja continua funcionando até ${formatDate(summary.tenant.endsAt)}.`}
                    {tenantStatus === 'EXPIRED' &&
                      'Contrate um plano para reativar o painel e voltar a receber pedidos.'}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {summary.subscription?.checkoutUrl && isOwner && (
                    <a
                      href={summary.subscription.checkoutUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      {summary.subscription.status === 'PAUSED'
                        ? 'Pagar fatura em aberto'
                        : tenantStatus === 'TRIAL'
                          ? 'Pagar agora (opcional)'
                          : 'Pagar fatura'}{' '}
                      <ExternalLink size={16} />
                    </a>
                  )}
                  {(pendingSubscription || activeSubscription) && (
                    <button onClick={handleSync} disabled={syncing} className="btn btn-outline">
                      <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} /> Atualizar
                      status
                    </button>
                  )}
                  {activeSubscription && isOwner && (
                    <button
                      onClick={handleCancel}
                      disabled={cancelling}
                      className="btn btn-ghost text-red-600"
                    >
                      {cancelling ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <XCircle size={16} />
                      )}{' '}
                      Cancelar assinatura
                    </button>
                  )}
                </div>
              </div>
              {activeSubscription && (
                <p className="mt-4 border-t border-gray-100 pt-4 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  Cobrança {activeSubscription.cycle === 'YEARLY' ? 'anual' : 'mensal'} de{' '}
                  {formatBRL(activeSubscription.amount)} (PIX, boleto ou cartão). Para trocar de
                  plano, cancele a assinatura atual e contrate o novo plano ou fale com nosso time.
                </p>
              )}
            </section>

            {/* Planos disponíveis */}
            {!activeSubscription && (
              <section>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Escolha seu plano
                  </h2>
                  {hasYearly && (
                    <div
                      className="inline-flex rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800"
                      role="group"
                    >
                      {(['MONTHLY', 'YEARLY'] as const).map(c => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setCycle(c)}
                          aria-pressed={cycle === c}
                          className={`rounded-md px-4 py-1.5 text-sm font-medium ${
                            cycle === c
                              ? 'bg-secondary-700 text-white'
                              : 'text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {c === 'MONTHLY' ? 'Mensal' : 'Anual (com desconto)'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {summary.gatewayEnabled && summary.documentRequired && isOwner && (
                  <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                    <label htmlFor="payer-document" className="form-label mb-1 block">
                      CPF ou CNPJ para as cobranças
                    </label>
                    <input
                      id="payer-document"
                      inputMode="numeric"
                      value={payerDocument}
                      onChange={e => {
                        setPayerDocument(maskDocument(e.target.value));
                        setDocumentError(null);
                      }}
                      placeholder="000.000.000-00 ou 00.000.000/0000-00"
                      className={`block w-full max-w-sm ${documentError ? 'border-error-500' : ''}`}
                      aria-invalid={!!documentError}
                    />
                    {documentError && (
                      <span className="form-error mt-1 block">{documentError}</span>
                    )}
                    <span className="form-hint mt-1 block">
                      Usado apenas para emitir as faturas da mensalidade.
                    </span>
                  </div>
                )}

                {!summary.gatewayEnabled && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-100">
                    <span>
                      A contratação online estará disponível em breve. Fale com nosso time para
                      ativar seu plano agora.
                    </span>
                    <Link to="/#contato" className="btn btn-primary text-sm">
                      <MessageCircle size={16} /> Falar com o time
                    </Link>
                  </div>
                )}

                <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {summary.plans.map(plan => {
                    const yearlyAvailable = plan.priceYearly !== null && plan.priceYearly > 0;
                    const showYearly = cycle === 'YEARLY' && yearlyAvailable;
                    const price = showYearly
                      ? (plan.priceYearly as number) / 12
                      : plan.priceMonthly;
                    const disabled =
                      !summary.gatewayEnabled ||
                      !isOwner ||
                      (cycle === 'YEARLY' && !yearlyAvailable);
                    return (
                      <div
                        key={plan.id}
                        className={`flex flex-col rounded-xl border bg-white p-6 dark:bg-gray-800 ${
                          plan.highlight
                            ? 'border-secondary-600 ring-1 ring-secondary-600'
                            : 'border-gray-200 dark:border-gray-700'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {plan.name}
                          </h3>
                          {plan.badge && (
                            <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-800 dark:bg-secondary-900/50 dark:text-secondary-200">
                              {plan.badge}
                            </span>
                          )}
                        </div>
                        <p className="mt-3">
                          <span className="text-3xl font-bold text-gray-900 dark:text-white">
                            {formatBRL(price)}
                          </span>
                          <span className="text-sm text-gray-500">/mês</span>
                        </p>
                        <p className="text-xs text-gray-500">
                          {showYearly
                            ? `${formatBRL(plan.priceYearly as number)} cobrados por ano`
                            : 'cobrança mensal'}
                          {cycle === 'YEARLY' && !yearlyAvailable && ' · sem opção anual'}
                        </p>
                        <ul className="mt-4 flex-1 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                          {plan.features.map(f => (
                            <li key={f} className="flex gap-2">
                              <Check
                                size={16}
                                className="mt-0.5 shrink-0 text-secondary-700 dark:text-secondary-400"
                              />{' '}
                              {f}
                            </li>
                          ))}
                        </ul>
                        <button
                          type="button"
                          onClick={() => handleSubscribe(plan.slug)}
                          disabled={disabled || busyPlan !== null}
                          className={`btn mt-6 w-full ${plan.highlight ? 'btn-primary' : 'btn-outline'}`}
                        >
                          {busyPlan === plan.slug ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : null}
                          Assinar {plan.name}
                        </button>
                      </div>
                    );
                  })}
                </div>

                {!isOwner && (
                  <p className="mt-3 text-sm text-gray-500">
                    Somente o dono do estabelecimento pode contratar ou cancelar planos.
                  </p>
                )}
                <p className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                  <ShieldCheck size={14} /> Pague por PIX, boleto ou cartão, com processamento
                  seguro. Se estiver no período de teste, a primeira cobrança só acontece quando ele
                  terminar. Sem fidelidade.
                </p>
              </section>
            )}
          </>
        ) : null}
      </main>
    </div>
  );
}
